import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api, ApiError, type PropertyDefView } from '../lib/api'
import { ReadOnlyNotice } from './ReadOnlyNotice'

const TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  single_select: 'Single-select',
  multi_select: 'Multi-select',
  file: 'File',
  cascading_select: 'Cascading dropdown',
}

/**
 * Where a workspace defines its own fields.
 *
 * The industry blueprint seeds a working set, but the point of this screen is that it stops
 * being ours: a firm that needs "Policy number" adds it here rather than waiting for us to
 * ship code. Grouped into sections because a flat list of fifteen fields is unusable in the
 * side panel where they end up.
 */
export function PropertySettings() {
  const queryClient = useQueryClient()
  const [entity, setEntity] = useState<'chat' | 'ticket'>('chat')
  const [adding, setAdding] = useState(false)
  const [managingSections, setManagingSections] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const defs = useQuery({ queryKey: ['property-defs'], queryFn: api.propertyDefs, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['property-defs'] })

  const remove = useMutation({ mutationFn: api.deletePropertyDef, onSuccess: refresh })
  const update = useMutation({
    mutationFn: (input: { id: string; requiredBeforeClose: boolean }) =>
      api.updatePropertyDef(input.id, { requiredBeforeClose: input.requiredBeforeClose }),
    onSuccess: refresh,
  })
  // A separate mutation from `update` above: that one only ever flips the "Required" pill and
  // shares its error banner with the rest of this screen, but a failed edit save needs to show
  // up inline in the still-open edit row, not in a banner the row itself doesn't render.
  const editDef = useMutation({
    mutationFn: (input: {
      id: string
      name: string
      section: string
      options: string[]
      cascadeOptions: string[][]
      hint: string
    }) =>
      api.updatePropertyDef(input.id, {
        name: input.name,
        section: input.section,
        options: input.options,
        cascadeOptions: input.cascadeOptions,
        hint: input.hint,
      }),
    onSuccess: () => {
      setEditingId(null)
      void refresh()
    },
  })
  const saveSections = useMutation({
    mutationFn: api.savePropertySections,
    onSuccess: async () => {
      setManagingSections(false)
      await refresh()
    },
  })

  if (defs.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (defs.isError) {
    const message = defs.error instanceof ApiError ? defs.error.message : 'Settings unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  // Server-sorted already: by explicit section order where one exists, then first-seen order
  // for a section nobody has touched yet — grouping here just has to preserve that order, not
  // recompute it.
  const all = defs.data!.defs.filter((d) => d.entity === entity)
  const canManage = defs.data!.canManage
  const sections = all.reduce<Record<string, typeof all>>((acc, def) => {
    ;(acc[def.section] ??= []).push(def)
    return acc
  }, {})
  const sectionNames = [...new Set(all.map((d) => d.section))]

  return (
    <>
      {!canManage && <ReadOnlyNotice what="custom fields" />}
      {/* The active tab above already says "Custom fields" — an eyebrow and a repeated
          title here would be the third place on screen naming the same thing. */}
      <p className="cad-muted" style={{ maxWidth: '62ch' }}>
        The fields your team fills in on a conversation or a ticket. Your industry set up a
        starting point — change it to match how you actually work.
      </p>

      <div className="cad-tabs" role="tablist">
        {(['chat', 'ticket'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            className="cad-tab"
            aria-selected={entity === option}
            onClick={() => {
              setEntity(option)
              setAdding(false)
            }}
          >
            {option === 'chat' ? 'On a conversation' : 'On a ticket'}
          </button>
        ))}
      </div>

      {canManage && sectionNames.length > 1 && (
        <div className="row">
          <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setManagingSections((v) => !v)}>
            {managingSections ? 'Close' : 'Reorder or rename sections'}
          </button>
        </div>
      )}

      {managingSections && (
        <SectionManager
          // Forces a fresh mount per entity: the manager seeds its order/renames state from
          // `names` only once, so reusing the instance across a chat-to-ticket tab switch left
          // the panel editing one entity's sections while claiming to be the other's.
          key={entity}
          entity={entity}
          names={sectionNames}
          pending={saveSections.isPending}
          error={saveSections.error}
          onSave={(names, renames) => saveSections.mutate({ entity, names, renames })}
          onCancel={() => setManagingSections(false)}
        />
      )}

      {(remove.error || update.error) && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {(() => {
            const err = remove.error ?? update.error
            return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
          })()}
        </p>
      )}

      {Object.entries(sections).map(([section, fields]) => (
        <section key={section} className="cad-card">
          <div className="stack gap-3">
            <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
              <h2 className="cad-section-title">{section}</h2>
              <span className="cad-pill">{fields.length} field{fields.length === 1 ? '' : 's'}</span>
            </div>

            <div className="cad-grid-frame">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    {entity === 'ticket' && <th>Required to close</th>}
                    {canManage && <th />}
                  </tr>
                </thead>
                <tbody>
                  {fields.map((def) =>
                    editingId === def.id ? (
                      <EditFieldRow
                        key={def.id}
                        def={def}
                        colSpan={entity === 'ticket' ? (canManage ? 4 : 3) : canManage ? 3 : 2}
                        sections={sectionNames}
                        saving={editDef.isPending}
                        error={editDef.error}
                        onSave={(fields) => editDef.mutate({ id: def.id, ...fields })}
                        onCancel={() => {
                          editDef.reset()
                          setEditingId(null)
                        }}
                      />
                    ) : (
                      <tr key={def.id}>
                        <td>
                          <div className="stack gap-1">
                            <span style={{ fontWeight: 500 }}>{def.name}</span>
                            {def.hint && <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{def.hint}</span>}
                            {def.options.length > 0 && (
                              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                                {def.options.join(' · ')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="cad-muted">{TYPE_LABELS[def.type] ?? def.type}</td>
                        {entity === 'ticket' && (
                          <td>
                            {canManage ? (
                              <button
                                type="button"
                                className="cad-pill"
                                data-tone={def.requiredBeforeClose ? 'accent' : undefined}
                                onClick={() =>
                                  update.mutate({
                                    id: def.id,
                                    requiredBeforeClose: !def.requiredBeforeClose,
                                  })
                                }
                              >
                                {def.requiredBeforeClose ? 'Required' : 'Optional'}
                              </button>
                            ) : (
                              <span className="cad-muted">
                                {def.requiredBeforeClose ? 'Required' : 'Optional'}
                              </span>
                            )}
                          </td>
                        )}
                        {canManage && (
                          <td className="num">
                            <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                className="cad-btn"
                                data-variant="text"
                                onClick={() => {
                                  editDef.reset()
                                  setEditingId(def.id)
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="cad-btn"
                                data-variant="text"
                                // Removing a definition cascades to its values (tenantSchema's
                                // FK is onDelete: 'cascade'), so this erases the answer from
                                // every ticket and contact that had one. It fired on a single
                                // click with no confirmation — strictly more destructive than
                                // deleting a label, which does confirm.
                                onClick={() => {
                                  const ok = confirm(
                                    `Remove "${def.name}"?\n\nIts value will be deleted from every ${
                                      entity === 'ticket' ? 'ticket' : 'chat'
                                    } that has one. This cannot be undone.`,
                                  )
                                  if (ok) remove.mutate(def.id)
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ))}

      {entity === 'ticket' && (
        <p className="cad-note">
          A field marked required blocks the ticket from being closed until it has a value.
          That is what makes the record exist when someone asks for it months later.
        </p>
      )}

      {canManage &&
        (adding ? (
          <AddField
            entity={entity}
            sections={sectionNames}
            onDone={() => {
              setAdding(false)
              void refresh()
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <div className="row">
            <button type="button" className="cad-btn" onClick={() => setAdding(true)}>
              Add a field
            </button>
          </div>
        ))}
    </>
  )
}

function SectionManager({
  entity,
  names,
  pending,
  error,
  onSave,
  onCancel,
}: {
  entity: 'chat' | 'ticket'
  names: string[]
  pending: boolean
  error: unknown
  onSave: (names: string[], renames: Record<string, string>) => void
  onCancel: () => void
}) {
  const [order, setOrder] = useState(names)
  const [labels, setLabels] = useState<Record<string, string>>(
    Object.fromEntries(names.map((n) => [n, n])),
  )

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= order.length) return
    setOrder((current) => {
      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <h2 className="cad-section-title">Sections {entity === 'chat' ? 'on a conversation' : 'on a ticket'}</h2>
        {order.map((original, index) => (
          <div key={original} className="row gap-2" style={{ alignItems: 'center' }}>
            <div className="stack gap-1" style={{ flexShrink: 0 }}>
              <button type="button" className="cad-btn" data-variant="text" disabled={index === 0} onClick={() => move(index, -1)}>
                ↑
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                disabled={index === order.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
            </div>
            <input
              className="cad-input"
              value={labels[original] ?? original}
              onChange={(e) => setLabels((current) => ({ ...current, [original]: e.target.value }))}
              style={{ flex: 1 }}
            />
          </div>
        ))}
        {error ? (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {error instanceof ApiError || error instanceof Error ? error.message : 'Could not save that.'}
          </p>
        ) : null}
        <div className="row gap-2">
          <button
            type="button"
            className="cad-btn"
            disabled={pending}
            onClick={() => {
              const renames = Object.fromEntries(
                order
                  .map((original) => [original, (labels[original] ?? original).trim()])
                  .filter(([original, next]) => next && next !== original),
              )
              const finalOrder = order.map((original) => (labels[original]?.trim() ? labels[original].trim() : original))
              onSave(finalOrder, renames)
            }}
          >
            {pending ? 'Saving…' : 'Save order and names'}
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  )
}

function AddField({
  entity,
  sections,
  onDone,
  onCancel,
}: {
  entity: 'chat' | 'ticket'
  sections: string[]
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [section, setSection] = useState(sections[0] ?? 'General')
  const [type, setType] = useState('text')
  const [optionsText, setOptionsText] = useState('')
  const [cascadeText, setCascadeText] = useState('')
  const [hint, setHint] = useState('')
  const [required, setRequired] = useState(false)

  const create = useMutation({
    mutationFn: () =>
      api.createPropertyDef({
        entity,
        name,
        section,
        type,
        options: optionsText
          .split('\n')
          .map((o) => o.trim())
          .filter(Boolean),
        cascadeOptions: cascadeText
          .split('\n')
          .map((line) => line.split('>').map((part) => part.trim()).filter(Boolean))
          .filter((path) => path.length > 0),
        hint,
        requiredBeforeClose: required,
      }),
    onSuccess: onDone,
  })

  const needsOptions = type === 'single_select' || type === 'multi_select'
  const needsCascade = type === 'cascading_select'
  const error = create.error
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <section className="cad-card">
      <form
        className="stack gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          if (name.trim()) create.mutate()
        }}
      >
        <h2 className="cad-section-title">
          New field on {entity === 'chat' ? 'a conversation' : 'a ticket'}
        </h2>

        <div className="field-grid">
          <div className="cad-field">
            <label htmlFor="pf-name">Name</label>
            <input
              id="pf-name"
              className="cad-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Policy number"
            />
          </div>

          <div className="cad-field">
            <label htmlFor="pf-section">Section</label>
            <input
              id="pf-section"
              className="cad-input"
              list="pf-sections"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              placeholder="Account"
            />
            {/* Existing sections offered, but a new one can be typed — grouping should not
                require a separate trip to another screen. */}
            <datalist id="pf-sections">
              {sections.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div className="cad-field">
            <label htmlFor="pf-type">Type</label>
            <select
              id="pf-type"
              className="cad-input"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="cad-field">
            <label htmlFor="pf-hint">Hint (optional)</label>
            <input
              id="pf-hint"
              className="cad-input"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="Shown under the field"
            />
          </div>
        </div>

        {needsOptions && (
          <div className="cad-field">
            <label htmlFor="pf-options">Options, one per line</label>
            <textarea
              id="pf-options"
              className="cad-input"
              rows={4}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              placeholder={'Upheld\nNot upheld\nReferred'}
            />
          </div>
        )}

        {needsCascade && (
          <div className="cad-field">
            <label htmlFor="pf-cascade">Option paths, one per line, up to three levels deep</label>
            <textarea
              id="pf-cascade"
              className="cad-input"
              rows={5}
              value={cascadeText}
              onChange={(e) => setCascadeText(e.target.value)}
              placeholder={'Electronics > Phones > Android\nElectronics > Phones > iOS\nElectronics > Laptops'}
            />
            <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              Separate each level with {'"'}&gt;{'"'}. Whoever fills this in picks a category,
              then only sees the subcategories that follow from it.
            </p>
          </div>
        )}

        {entity === 'ticket' && (
          <label className="row gap-2" style={{ alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            <span>Must be filled before the ticket can be closed</span>
          </label>
        )}

        {message && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {message}
          </p>
        )}

        <div className="row gap-2">
          <button type="submit" className="cad-btn" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add field'}
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  )
}

/**
 * Inline edit for one existing field — name, section, hint, and its options (if it has any).
 * `type` is intentionally not offered here: changing it out from under answers already recorded
 * against this field is what the delete-and-recreate path did before, and that's the loss this
 * screen exists to avoid, not something to make easier to trigger by accident.
 */
function EditFieldRow({
  def,
  colSpan,
  sections,
  saving,
  error,
  onSave,
  onCancel,
}: {
  def: PropertyDefView
  colSpan: number
  sections: string[]
  saving: boolean
  error: unknown
  onSave: (fields: { name: string; section: string; options: string[]; cascadeOptions: string[][]; hint: string }) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(def.name)
  const [section, setSection] = useState(def.section)
  const [hint, setHint] = useState(def.hint ?? '')
  const [optionsText, setOptionsText] = useState(def.options.join('\n'))
  const [cascadeText, setCascadeText] = useState(
    def.cascadeOptions.map((path) => path.join(' > ')).join('\n'),
  )

  const needsOptions = def.type === 'single_select' || def.type === 'multi_select'
  const needsCascade = def.type === 'cascading_select'
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <tr>
      <td colSpan={colSpan}>
        <div className="stack gap-3" style={{ padding: '8px 0' }}>
          <div className="field-grid">
            <div className="cad-field">
              <label htmlFor={`pf-edit-name-${def.id}`}>Name</label>
              <input
                id={`pf-edit-name-${def.id}`}
                className="cad-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="cad-field">
              <label htmlFor={`pf-edit-section-${def.id}`}>Section</label>
              <input
                id={`pf-edit-section-${def.id}`}
                className="cad-input"
                list={`pf-edit-sections-${def.id}`}
                value={section}
                onChange={(e) => setSection(e.target.value)}
              />
              <datalist id={`pf-edit-sections-${def.id}`}>
                {sections.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>
            <div className="cad-field">
              <label htmlFor={`pf-edit-hint-${def.id}`}>Hint (optional)</label>
              <input
                id={`pf-edit-hint-${def.id}`}
                className="cad-input"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
              />
            </div>
          </div>

          {needsOptions && (
            <div className="cad-field">
              <label htmlFor={`pf-edit-options-${def.id}`}>Options, one per line</label>
              <textarea
                id={`pf-edit-options-${def.id}`}
                className="cad-input"
                rows={4}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
              />
            </div>
          )}

          {needsCascade && (
            <div className="cad-field">
              <label htmlFor={`pf-edit-cascade-${def.id}`}>Option paths, one per line</label>
              <textarea
                id={`pf-edit-cascade-${def.id}`}
                className="cad-input"
                rows={5}
                value={cascadeText}
                onChange={(e) => setCascadeText(e.target.value)}
              />
            </div>
          )}

          {message && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {message}
            </p>
          )}

          <div className="row gap-2">
            <button
              type="button"
              className="cad-btn"
              disabled={!name.trim() || !section.trim() || saving}
              onClick={() =>
                onSave({
                  name: name.trim(),
                  section: section.trim(),
                  options: optionsText.split('\n').map((o) => o.trim()).filter(Boolean),
                  cascadeOptions: cascadeText
                    .split('\n')
                    .map((line) => line.split('>').map((part) => part.trim()).filter(Boolean))
                    .filter((path) => path.length > 0),
                  hint,
                })
              }
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  )
}
